import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import Icon from '@components/icon/Icon';
import { mdiMagnify, mdiClose } from '@mdi/js';
import classNames from 'classnames';
import Tooltip from '@components/tooltip/tooltip';
import { isValidText, sanitizeText } from '@src/utils/helpers';
import _debounce from 'lodash/debounce';
import genericSecondaryModal from '@forms/components/modals/genericSecondaryModal';
import { ErrorMessages } from '@src/utils/enums';
import { TextInput, Label } from '@trussworks/react-uswds';
import { useGeospatial, useModal } from '@stores';
import './addressSuggestInput.scss';
import { useSuggest, useAddressCandidatesMutation } from '@hooks';
import Loader from '../loader/Loader';

const AddressSuggestInput = ({
  className = 'width-full',
  showOptionalText = true,
  isProjectAddress = false,
  name,
  label,
  onChange = () => {},
  onBlur = () => {},
  populateAddressFields = () => {},
  required,
  tooltip,
  tooltipClickable,
  readOnly,
  usOnly = true,
  uppercase,
  customProps,
}) => {
  const [currentIndex, setCurrentIndex] = useState(-1);
  const [showAddressSuggest, setShowAddressSuggest] = useState(false);
  const [userLocation, setUserLocation] = useState();
  const [searchText, setSearchText] = useState();

  const { openSecondaryModal } = useModal();
  const { mutateAsync: getAddressCandidatesAsync } = useAddressCandidatesMutation();
  const { addressSuggestions, setAddressSuggestions, setAddressCandidates } = useGeospatial();
  const resultsRef = useRef();
  const {
    register,
    watch,
    getValues,
    formState: { errors },
  } = useFormContext();

  const inputError = errors[name];
  const inputValue = watch(name);
  const inputStyles = classNames(className, 'address-search-input', { 'text-uppercase': uppercase });
  const iconStyles = classNames('icon-search', {
    'is-invalid': inputError,
  });
  const labelStyles = classNames({
    required: required,
    optional: !required && showOptionalText,
  });

  const { data: suggestionsData, isFetching: isLoadingSuggestData } = useSuggest(searchText, userLocation, usOnly);

  const debouncedSetSearchText = useMemo(
    () =>
      _debounce((value) => {
        if (isValidText(value)) {
          setSearchText(value);
        } else {
          openSecondaryModal(genericSecondaryModal, {
            title: 'Invalid Characters',
            msg: ErrorMessages.InvalidCharacters,
          });
          setSearchText(sanitizeText(value));
        }
      }, 300),
    [openSecondaryModal]
  );

  const getAddressSuggestion = (e) => {
    if (navigator?.geolocation && !userLocation) {
      navigator.geolocation.getCurrentPosition((location) =>
        setUserLocation(`${location?.coords?.longitude},${location?.coords?.latitude}`)
      );
    }

    const searchText = e?.target?.value;

    if (!searchText?.length || searchText.length < 3) {
      setAddressSuggestions([]);
      setShowAddressSuggest(false);
    } else if (searchText?.length > 1) {
      debouncedSetSearchText(searchText, userLocation ?? '-113,44', usOnly);
      !showAddressSuggest && addressSuggestions && setShowAddressSuggest(true);
    }
  };

  const selectAddress = useCallback(
    async (e) => {
      if (typeof e == 'undefined') {
        setAddressCandidates([]);
        setAddressSuggestions([]);
        setShowAddressSuggest(false);
      } else {
        const address = e?.[1];
        const magicKey = e?.[0];
        address &&
          (await getAddressCandidatesAsync({ address, magicKey })
            .then((data) => {
              populateAddressFields && populateAddressFields(data, isProjectAddress);
            })
            .catch((error) => {
              console.error('Error fetching address candidates: ', error);
            }));
        setAddressSuggestions([]);
        setShowAddressSuggest(false);
      }
    },
    [getAddressCandidatesAsync, isProjectAddress, populateAddressFields, setAddressCandidates, setAddressSuggestions]
  );

  const handleOutsideClick = useCallback((e) => {
    if (resultsRef?.current && !resultsRef.current.contains(e.target)) {
      setShowAddressSuggest(false);
    }
  }, []);

  const handleKeyPress = useCallback(
    (e) => {
      if (!showAddressSuggest) return;

      const items = resultsRef?.current?.querySelectorAll('.dropdown-item');
      switch (e?.key) {
        case 'ArrowDown':
          e?.preventDefault();
          setCurrentIndex((prevIndex) => (prevIndex + 1) % items?.length);
          break;
        case 'ArrowUp':
          e?.preventDefault();
          setCurrentIndex((prevIndex) => (prevIndex - 1 + items?.length) % items?.length);
          break;
        case 'Enter':
          e?.preventDefault();
          if (currentIndex >= 0 && currentIndex < items?.length) {
            items?.[currentIndex]?.click();
            setCurrentIndex(-1);
          }
          break;
        case 'Escape':
          e?.preventDefault();
          setCurrentIndex(-1);
          break;
        default:
          break;
      }
    },
    [currentIndex, showAddressSuggest]
  );

  useEffect(() => {
    if (inputValue !== 'string') return;

    debouncedSetSearchText(inputValue);
  }, [inputValue, debouncedSetSearchText]);

  useEffect(() => {
    document.addEventListener('click', handleOutsideClick);
    document.addEventListener('keydown', handleKeyPress);

    return () => {
      document.removeEventListener('click', handleOutsideClick);
      document.removeEventListener('keydown', handleKeyPress);
    };
  }, [handleKeyPress, handleOutsideClick]);

  useEffect(() => {
    if (currentIndex !== -1 && showAddressSuggest) {
      const items = resultsRef.current.querySelectorAll('.dropdown-item');
      if (items[currentIndex]) {
        items[currentIndex].focus();
      }
    }
  }, [currentIndex, showAddressSuggest]);

  useEffect(() => {
    setAddressSuggestions(suggestionsData);
    suggestionsData?.length && setShowAddressSuggest(true);
  }, [setAddressSuggestions, suggestionsData]);

  const handleCloseSuggestions = () => {
    setAddressSuggestions([]);
    setShowAddressSuggest(false);
  };

  const handleChange = (e) => {
    getAddressSuggestion(e);
    onChange(e);
    debouncedSetSearchText(e?.target?.value);
  };

  const { ref: addressSuggestionRef, ...rest } = register(name, { onBlur: onBlur, onChange: handleChange });

  return (
    <>
      <div className='address-suggest-container width-full'>
        <Label htmlFor={name} className={labelStyles}>
          <span id={`${name}_label`}>{label}</span>
          {tooltip && <Tooltip clickable={tooltipClickable} content={tooltip} header={label} name={name} />}
        </Label>
        <TextInput
          autoComplete='off'
          className={inputStyles}
          type='text'
          defaultValue={getValues(name)}
          readOnly={readOnly}
          id={name}
          inputRef={addressSuggestionRef}
          name={name}
          required={required}
          validationStatus={inputError && 'error'}
          aria-label='Search Icon with result cancel'
          aria-required={required ? 'true' : 'false'}
          aria-invalid={inputError ? 'true' : 'false'}
          aria-describedby={name}
          {...rest}
          {...customProps}
        />
        <Icon className={iconStyles} focusable={false} path={mdiMagnify} size={1} />
      </div>
      {isLoadingSuggestData && (
        <div className='address-suggestions'>
          <div className='dropdown-content'>
            <Loader variant='section' />
          </div>
        </div>
      )}
      {!isLoadingSuggestData && showAddressSuggest && (
        <div className='address-suggestions'>
          <div className='dropdown-content' ref={resultsRef} role='listbox'>
            <div className='suggestion-title'>
              Suggestions
              <Icon path={mdiClose} className='close-btn' onClick={handleCloseSuggestions} size={'16px'} />
            </div>
            {addressSuggestions?.suggestions?.map((res) => (
              <div
                className='dropdown-item'
                key={res.magicKey}
                onClick={() => selectAddress([res.magicKey, res.text])}
                tabIndex={0}
                aria-selected='false'
                role='option'
              >
                {res.text}
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
};

export default AddressSuggestInput;
